<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const selectedMode = ref('person');
const isSubmitting = ref(false);

const { currentAccount, updateAccount } = useAccount();

const options = [
  {
    value: 'person',
    labelKey: 'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_PERSON',
    descKey:
      'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_PERSON_DESC',
  },
  {
    value: 'team',
    labelKey: 'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_TEAM',
    descKey: 'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_TEAM_DESC',
  },
  {
    value: 'both',
    labelKey: 'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_BOTH',
    descKey: 'GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.OPTION_BOTH_DESC',
  },
];

watch(
  currentAccount,
  () => {
    const mode =
      currentAccount.value?.settings?.assignment_consideration || 'person';
    selectedMode.value = mode;
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      { assignment_consideration: selectedMode.value },
      { silent: true }
    );
    useAlert(t('GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.API.SUCCESS'));
  } catch (error) {
    useAlert(t('GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.API.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.TITLE')"
    :description="
      t('GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.DESCRIPTION')
    "
    with-border
  >
    <form class="grid gap-4" @submit.prevent="handleSubmit">
      <div
        class="rounded-xl border border-n-weak bg-n-solid-1 w-full text-sm text-n-slate-12 divide-y divide-n-weak"
      >
        <label
          v-for="option in options"
          :key="option.value"
          class="p-3 flex items-start gap-3 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          :class="{ 'bg-n-alpha-1': selectedMode === option.value }"
        >
          <input
            v-model="selectedMode"
            type="radio"
            name="assignment_consideration"
            :value="option.value"
            class="mt-0.5 accent-woot-500"
          />
          <div>
            <span class="font-medium">{{ t(option.labelKey) }}</span>
            <p class="text-n-slate-11 text-xs mt-0.5">
              {{ t(option.descKey) }}
            </p>
          </div>
        </label>
      </div>
      <div class="flex gap-2">
        <NextButton
          blue
          type="submit"
          :is-loading="isSubmitting"
          :label="
            t('GENERAL_SETTINGS.FORM.ASSIGNMENT_CONSIDERATION.UPDATE_BUTTON')
          "
        />
      </div>
    </form>
  </SectionLayout>
</template>
